import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight, MapPin, Phone } from 'lucide-react';
import { DELIVERY_FLOW, DELIVERY_SLOTS, DELIVERY_STATUSES, formatQty, type DeliveryStatus } from '@tengesa/shared';
import { api } from '../../lib/api';
import { addDays, todayLocal } from '../../lib/dates';
import { useSession } from '../../app/session';
import { Badge, Button, Dialog, EmptyState, ErrorNotice, Field, FormError, PageHeader, Panel, Select, Spinner, TextInput, fieldErrors, formatDateTime, useToast } from '../../ui';

type Slot = keyof typeof DELIVERY_SLOTS;
interface Delivery {
  id: string;
  number: string;
  branchId: string;
  branchName: string;
  orderId: string | null;
  saleId: string | null;
  reference: string | null;
  customerName: string | null;
  address: string;
  contactName: string | null;
  contactPhone: string;
  scheduledDate: string;
  slot: Slot;
  driverName: string | null;
  vehicle: string | null;
  status: DeliveryStatus;
  notes: string | null;
  receivedBy: string | null;
  deliveredAt: string | null;
  history: { status: DeliveryStatus; at: string; by: string; note: string | null }[];
  items: { name: string; qtyMilli: number }[];
}

const TONE: Record<DeliveryStatus, 'gold' | 'ok' | 'danger' | 'neutral'> = { scheduled: 'neutral', out_for_delivery: 'gold', delivered: 'ok', failed: 'danger', cancelled: 'danger' };
const longDate = (d: string) => new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(new Date(`${d}T00:00:00Z`));

export function DeliveriesPage() {
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'outstanding' ? 'outstanding' : 'day';
  const date = params.get('date') ?? todayLocal();
  const go = (p: Record<string, string>) => setParams(p, { replace: true });
  const q = useQuery({
    queryKey: ['deliveries', view, date],
    queryFn: () => api.get<Delivery[]>(view === 'day' ? `/api/deliveries?from=${date}&to=${date}` : '/api/deliveries?status=pending'),
  });
  const [acting, setActing] = useState<{ d: Delivery; to: DeliveryStatus | 'reschedule' } | null>(null);
  const bySlot = (Object.keys(DELIVERY_SLOTS) as Slot[]).map((s) => ({ slot: s, items: (q.data ?? []).filter((d) => d.slot === s) }));
  return (
    <>
      <PageHeader
        title="Deliveries"
        description="Everything going out on the truck. Schedule a delivery from an order or a sale, then follow it here until someone signs for it."
      />
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <Select aria-label="View" className="max-w-72" value={view} onChange={(e) => go(e.target.value === 'outstanding' ? { view: 'outstanding' } : { date })}>
          <option value="day">One day</option>
          <option value="outstanding">Everything not yet delivered</option>
        </Select>
        {view === 'day' && (
          <>
            <Button size="md" aria-label="Previous day" onClick={() => go({ date: addDays(date, -1) })} icon={<ChevronLeft className="size-4" />} />
            <TextInput aria-label="Date" type="date" className="max-w-44" value={date} onChange={(e) => e.target.value && go({ date: e.target.value })} />
            <Button size="md" aria-label="Next day" onClick={() => go({ date: addDays(date, 1) })} icon={<ChevronRight className="size-4" />} />
            {date !== todayLocal() && <Button variant="ghost" onClick={() => go({ date: todayLocal() })}>Today</Button>}
            <span className="ml-1 text-muted">{longDate(date)}</span>
          </>
        )}
      </div>
      {q.isLoading && <Spinner />}
      {q.error && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      {q.data && q.data.length === 0 && (
        <Panel>
          <EmptyState title={view === 'day' ? 'No deliveries on this day' : 'Nothing waiting to go out'}>Deliveries are scheduled from an order or a sale.</EmptyState>
        </Panel>
      )}
      {q.data && q.data.length > 0 && view === 'day' && (
        <div className="space-y-6">
          {bySlot.filter((s) => s.items.length).map((s) => (
            <section key={s.slot}>
              <h2 className="mb-2 text-sm font-medium text-muted">{DELIVERY_SLOTS[s.slot]} — {s.items.length}</h2>
              <div className="grid gap-3 md:grid-cols-2">{s.items.map((d) => <DeliveryCard key={d.id} d={d} onAct={(to) => setActing({ d, to })} />)}</div>
            </section>
          ))}
        </div>
      )}
      {q.data && q.data.length > 0 && view === 'outstanding' && (
        <div className="grid gap-3 md:grid-cols-2">{q.data.map((d) => <DeliveryCard key={d.id} d={d} showDate onAct={(to) => setActing({ d, to })} />)}</div>
      )}
      {acting && <UpdateDialog delivery={acting.d} to={acting.to} onClose={() => setActing(null)} />}
    </>
  );
}

function DeliveryCard({ d, showDate, onAct }: { d: Delivery; showDate?: boolean; onAct: (to: DeliveryStatus | 'reschedule') => void }) {
  const [open, setOpen] = useState(false);
  const next = DELIVERY_FLOW[d.status];
  return (
    <article className={clsx('rounded-[var(--radius-panel)] border bg-surface', d.status === 'out_for_delivery' ? 'border-gold' : 'border-line')}>
      <div className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
        <div className="min-w-0">
          <p className="font-medium">
            {d.customerName ?? d.contactName ?? 'Customer'} <span className="text-sm font-normal text-muted">{d.number}</span>
          </p>
          <p className="text-sm text-muted">
            {d.reference && (d.orderId ? <Link className="hover:underline" to={`/orders/${d.orderId}`}>{d.reference}</Link> : <Link className="hover:underline" to={`/sales/${d.saleId}`}>{d.reference}</Link>)}
            {showDate && ` — ${d.scheduledDate}, ${DELIVERY_SLOTS[d.slot]}`}
            {` — from ${d.branchName}`}
          </p>
        </div>
        <Badge tone={TONE[d.status]}>{DELIVERY_STATUSES[d.status]}</Badge>
      </div>
      <div className="space-y-2 px-4 py-3 text-sm">
        <p className="flex gap-2"><MapPin className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden />{d.address}</p>
        <p className="flex gap-2">
          <Phone className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden />
          <a className="hover:underline" href={`tel:${d.contactPhone.replace(/\s/g, '')}`}>{d.contactPhone}</a>
          {d.contactName && <span className="text-muted">({d.contactName})</span>}
        </p>
        <ul className="rounded-[8px] bg-paper px-3 py-2">
          {d.items.map((i, n) => <li key={n}><span className="num">{formatQty(i.qtyMilli)}</span> × {i.name}</li>)}
        </ul>
        {(d.driverName || d.vehicle) && <p className="text-muted">Driver: {[d.driverName, d.vehicle].filter(Boolean).join(', ')}</p>}
        {d.notes && <p className="text-muted">{d.notes}</p>}
        {d.receivedBy && <p>Received by <span className="font-medium">{d.receivedBy}</span>, {formatDateTime(d.deliveredAt)}</p>}
        <button className="text-sm text-ledger-700 hover:underline" onClick={() => setOpen((o) => !o)}>{open ? 'Hide history' : 'History'}</button>
        {open && (
          <ol className="space-y-1 border-l-2 border-line pl-3 text-xs text-muted">
            {d.history.map((h, i) => (
              <li key={i}>
                <span className="font-medium text-ink">{DELIVERY_STATUSES[h.status]}</span> — {formatDateTime(h.at)} by {h.by}{h.note ? `: ${h.note}` : ''}
              </li>
            ))}
          </ol>
        )}
      </div>
      {next.length > 0 && (
        <div className="flex flex-wrap justify-end gap-2 border-t border-line px-4 py-3">
          {next.includes('cancelled') && <Button size="sm" variant="ghost" onClick={() => onAct('cancelled')}>Cancel</Button>}
          {next.includes('scheduled') && <Button size="sm" onClick={() => onAct('reschedule')}>{d.status === 'failed' ? 'Try again on…' : 'Change day'}</Button>}
          {next.includes('failed') && <Button size="sm" onClick={() => onAct('failed')}>Could not deliver</Button>}
          {next.includes('out_for_delivery') && <Button size="sm" variant="primary" onClick={() => onAct('out_for_delivery')}>Send out</Button>}
          {next.includes('delivered') && <Button size="sm" variant="primary" onClick={() => onAct('delivered')}>Delivered</Button>}
        </div>
      )}
    </article>
  );
}

const TITLES: Record<DeliveryStatus | 'reschedule', string> = {
  reschedule: 'Change the delivery day',
  scheduled: 'Change the delivery day',
  out_for_delivery: 'Send out for delivery',
  delivered: 'Mark as delivered',
  failed: 'Could not deliver',
  cancelled: 'Cancel this delivery',
};

function UpdateDialog({ delivery, to, onClose }: { delivery: Delivery; to: DeliveryStatus | 'reschedule'; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const status: DeliveryStatus = to === 'reschedule' ? 'scheduled' : to;
  const [v, setV] = useState({
    scheduledDate: delivery.status === 'failed' ? addDays(todayLocal(), 1) : delivery.scheduledDate,
    slot: delivery.slot,
    driverName: delivery.driverName ?? '',
    vehicle: delivery.vehicle ?? '',
    receivedBy: '',
    note: '',
  });
  const set = <K extends keyof typeof v>(k: K, val: (typeof v)[K]) => setV((p) => ({ ...p, [k]: val }));
  const m = useMutation({
    mutationFn: () =>
      api.put(`/api/deliveries/${delivery.id}`, {
        status,
        ...(status === 'scheduled' ? { scheduledDate: v.scheduledDate, slot: v.slot } : {}),
        ...(status === 'out_for_delivery' ? { driverName: v.driverName, vehicle: v.vehicle } : {}),
        receivedBy: status === 'delivered' ? v.receivedBy : undefined,
        note: v.note || null,
      }),
    onSuccess: () => {
      toast(`${delivery.number}: ${status === 'scheduled' ? `moved to ${v.scheduledDate}` : DELIVERY_STATUSES[status].toLowerCase()}`);
      for (const k of ['deliveries', 'order']) void qc.invalidateQueries({ queryKey: [k] });
      onClose();
    },
  });
  const f = fieldErrors(m.error);
  const ok = status === 'delivered' ? v.receivedBy.trim().length > 0 : status === 'failed' || status === 'cancelled' ? v.note.trim().length >= 3 : true;
  return (
    <Dialog
      open
      onClose={onClose}
      title={TITLES[to]}
      description={`${delivery.number} to ${delivery.customerName ?? delivery.address}`}
      footer={
        <>
          <Button onClick={onClose}>Back</Button>
          <Button variant={status === 'cancelled' ? 'danger' : 'primary'} disabled={!ok} busy={m.isPending} onClick={() => m.mutate()}>{TITLES[to]}</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <div className="grid gap-4 sm:grid-cols-2">
        {status === 'scheduled' && (
          <>
            <Field label="Day" htmlFor="du-date" error={f.scheduledDate}>
              <TextInput id="du-date" type="date" value={v.scheduledDate} onChange={(e) => set('scheduledDate', e.target.value)} />
            </Field>
            <Field label="Time" htmlFor="du-slot">
              <Select id="du-slot" value={v.slot} onChange={(e) => set('slot', e.target.value as Slot)}>
                {Object.entries(DELIVERY_SLOTS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </Select>
            </Field>
          </>
        )}
        {status === 'out_for_delivery' && (
          <>
            <Field label="Driver" htmlFor="du-driver">
              <TextInput id="du-driver" value={v.driverName} onChange={(e) => set('driverName', e.target.value)} />
            </Field>
            <Field label="Vehicle" htmlFor="du-veh">
              <TextInput id="du-veh" value={v.vehicle} onChange={(e) => set('vehicle', e.target.value)} placeholder="e.g. AEZ 4417" />
            </Field>
          </>
        )}
        {status === 'delivered' && (
          <Field label="Received by" htmlFor="du-recv" error={f.receivedBy} className="sm:col-span-2" hint="The name of the person who signed for the goods">
            <TextInput id="du-recv" value={v.receivedBy} onChange={(e) => set('receivedBy', e.target.value)} />
          </Field>
        )}
        <Field
          label={status === 'failed' ? 'What happened?' : status === 'cancelled' ? 'Why is it cancelled?' : 'Note (optional)'}
          htmlFor="du-note"
          error={f.note}
          className="sm:col-span-2"
        >
          <TextInput id="du-note" value={v.note} onChange={(e) => set('note', e.target.value)} placeholder={status === 'failed' ? 'e.g. Nobody home, phone off' : undefined} />
        </Field>
      </div>
    </Dialog>
  );
}

/** Schedules a delivery for an order or a completed sale. */
export function DeliveryDialog({
  order,
  sale,
  onClose,
  onDone,
}: {
  order?: { id: string; number: string; branchId: string; customerName: string; customerPhone: string | null; customerAddress: string | null };
  sale?: { id: string; number: string; branchId: string; customerName: string | null; customerPhone: string | null };
  onClose: () => void;
  onDone: () => void;
}) {
  const { me } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [v, setV] = useState({
    address: order?.customerAddress ?? '',
    contactName: order?.customerName ?? sale?.customerName ?? '',
    contactPhone: order?.customerPhone ?? sale?.customerPhone ?? '',
    scheduledDate: addDays(todayLocal(), 1),
    slot: 'morning' as Slot,
    driverName: '',
    notes: '',
  });
  const set = <K extends keyof typeof v>(k: K, val: (typeof v)[K]) => setV((p) => ({ ...p, [k]: val }));
  const branchId = order?.branchId ?? sale!.branchId;
  const m = useMutation({
    mutationFn: () => api.post<{ number: string }>('/api/deliveries', { branchId, orderId: order?.id ?? null, saleId: sale?.id ?? null, ...v }),
    onSuccess: (d) => {
      toast(`${d.number} scheduled for ${v.scheduledDate}`);
      void qc.invalidateQueries({ queryKey: ['deliveries'] });
      onDone();
    },
  });
  const f = fieldErrors(m.error);
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Deliver ${order?.number ?? sale?.number}`}
      description={`From ${me.branches.find((b) => b.id === branchId)?.name ?? 'the branch'}.`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={v.address.trim().length < 5 || v.contactPhone.trim().length < 6} busy={m.isPending} onClick={() => m.mutate()}>Schedule delivery</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Delivery address" htmlFor="dd-addr" error={f.address} className="sm:col-span-2">
          <TextInput id="dd-addr" value={v.address} onChange={(e) => set('address', e.target.value)} placeholder="House number, street, suburb" />
        </Field>
        <Field label="Contact name" htmlFor="dd-name">
          <TextInput id="dd-name" value={v.contactName} onChange={(e) => set('contactName', e.target.value)} />
        </Field>
        <Field label="Contact phone" htmlFor="dd-phone" error={f.contactPhone}>
          <TextInput id="dd-phone" type="tel" value={v.contactPhone} onChange={(e) => set('contactPhone', e.target.value)} />
        </Field>
        <Field label="Day" htmlFor="dd-date" error={f.scheduledDate}>
          <TextInput id="dd-date" type="date" value={v.scheduledDate} onChange={(e) => set('scheduledDate', e.target.value)} />
        </Field>
        <Field label="Time" htmlFor="dd-slot">
          <Select id="dd-slot" value={v.slot} onChange={(e) => set('slot', e.target.value as Slot)}>
            {Object.entries(DELIVERY_SLOTS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </Select>
        </Field>
        <Field label="Notes for the driver (optional)" htmlFor="dd-notes" className="sm:col-span-2">
          <TextInput id="dd-notes" value={v.notes} onChange={(e) => set('notes', e.target.value)} placeholder="e.g. Second gate, call on arrival" />
        </Field>
      </div>
    </Dialog>
  );
}
